import {ChangeDetectorRef, Input, OnDestroy, OnInit} from '@angular/core';
import {FilesUploaderService} from './files-uploader.service';
import {Subject} from 'rxjs';
import {debounceTime, filter, map, takeUntil} from 'rxjs/operators';
import {QueueItem} from './queue-item';
import {UploadedFile, UploadedFileNamespace} from '@interfaces/file.interface';
import {FormControl, FormGroup} from '@angular/forms';
import {MimeTypeNamespace} from '@namespaces/mime-type.namespace';


/**
 * Файл для просмотра
 */
interface FilePreview {
    /**
     * Mime тип файла
     */
    type: MimeTypeNamespace.MimeEnums;

    /**
     * Имя исходного файла
     */
    name: string;

    /**
     * Ссылка на файл
     */
    url: string;

    /**
     * Размер файла в байтах
     */
    size: number;

    rawFile: UploadedFile;

    /**
     * Файл является картинкой?
     */
    isImage?: boolean;

    /**
     * Файл можно посмотреть?
     */
    isCanPreview?: boolean;

    iconUrl?: string;
}


export abstract class FileUploaderClassComponent implements OnInit, OnDestroy {
    @Input() fbGroup: FormGroup = new FormGroup({});

    /**
     * Максимальный размер загружаемого файла в мегабайтах
     */
    @Input() maxSize = 10;

    @Input() uploadStorageTarget: 'Noap_public' | 'Noap' | 'Lab' = 'Lab';

    /**
     * Наименование кнопки виджета
     */
    @Input() buttonName = 'Загрузить файл';

    /**
     * Mime типы принимаемых файлов
     */
    abstract accept: string[];

    /**
     * Файл очереди загрузки
     */
    public queueItem: QueueItem;

    /**
     * Данные предпросмотра файла
     */
    public filePreview: FilePreview;

    protected updateView$ = new Subject();

    protected destroyed$ = new Subject();

    protected constructor(
        protected uploader: FilesUploaderService,
        protected cd: ChangeDetectorRef,
    ) {
        this.updateView$.pipe(
            debounceTime(10),
            takeUntil(this.destroyed$),
        ).subscribe(() => this.cd.detectChanges());

        this.uploader.updateProgress$.pipe(
            takeUntil(this.destroyed$),
        ).subscribe(() => this.updateView$.next());
    }

    ngOnInit() {
        this.validateFormGroupStructure();

        /**
         * Подготовавливаем превью файла, основываясь на данных формы
         */
        this.fbGroup.valueChanges.pipe(
            map((v: UploadedFile): UploadedFile|null => {
                /**
                 * Файл считаем валидным, если у него присутствуют и заполнены все поля,
                 * иначе прокидываем null вместо файла
                 */
                const valid = UploadedFileNamespace.getUploadedFileFields().every((name: string) => {
                    return Boolean(v.hasOwnProperty(name) && v[name]);
                });
                return valid ? v : null;
            }),
            map((v: UploadedFile): FilePreview => {
                if (!v) {
                    return null;
                }

                const mime = v.file_mime_type;
                const isImage: boolean = MimeTypeNamespace.isImage(mime);
                const iconUrl: string = MimeTypeNamespace.getMimeImageUrl(mime);
                const isCanPreview: boolean = MimeTypeNamespace.isFilePreviewableInBrowser(mime);

                return {
                    type: mime,
                    name: v.file_name,
                    url: v.file_url,
                    size: v.file_size,
                    rawFile: v,
                    isImage,
                    isCanPreview,
                    iconUrl,
                };
            }),
            takeUntil(this.destroyed$),
        ).subscribe((filePreview: FilePreview|null) => {
            this.filePreview = filePreview;
            this.updateView$.next();
        }, () => {
            this.filePreview = null;
            this.updateView$.next();
        });

        this.fbGroup.updateValueAndValidity();
    }

    public onFilesSelect(list: FileList) {
        const file: File = list[0];

        if (!file) {
            this.updateView$.next();
            return;
        }

        /**
         * Создаем файл очереди
         */
        this.queueItem = new QueueItem(file, file.name);

        if (!this.validateFile(this.queueItem)) {
            this.updateView$.next();
            return;
        }

        this.afterSelectedFileValidation();
    }

    /**
     * Действие после валидации выбранного файла.
     * Можно переопределить, чтобы, например, вызвать кроппе для картинок
     * или выполнить другие действия перед загрузкой
     */
    public afterSelectedFileValidation() {
        this.uploadSingle();
    }

    public clear() {
        this.fbGroup.patchValue(UploadedFileNamespace.getEmptyFileStructure());
    }

    protected validateFile(item: QueueItem): boolean {
        const size = item.file.size;

        if ((size / 1024 / 1024) > this.maxSize) {
            item.setError('Размер файла превышает допустимый');
            return false;
        }

        if (!this.accept.includes(item.file.type)) {
            item.setError('Выбранный файл имеет недопустимый формат');
            return false;
        }

        return true;
    }

    /**
     * Провалидировать и восстановить структуру группы полей для работы с файлами
     */
    protected validateFormGroupStructure() {
        if (!this.fbGroup) {
            this.fbGroup = new FormGroup({});
        }

        UploadedFileNamespace.getUploadedFileFields().forEach(field => {
            if (!this.fbGroup.contains(field)) {
                this.fbGroup.setControl(field, new FormControl(null));
            }
        });
    }

    protected uploadSingle(): void {
        this.uploader.upload(this.queueItem, this.uploadStorageTarget).toPromise().then((file: UploadedFile) => {
            this.fbGroup.patchValue(file as any);
            this.queueItem = null;
            this.updateView$.next();
        }, () => {
            this.queueItem.setError('Не удалось загрузить файл');
            this.updateView$.next();
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }
}
